import React, { useEffect, useMemo, useRef, useState } from "react";
import { getActiveBranch } from "../apiBase";

function safeLower(v) {
  return String(v || "").trim().toLowerCase();
}

function fmtBytes(n) {
  const x = Number(n || 0);
  if (x < 1024) return `${x} B`;
  if (x < 1024 * 1024) return `${(x / 1024).toFixed(1)} KB`;
  return `${(x / (1024 * 1024)).toFixed(2)} MB`;
}

function fmtWhen(ts) {
  if (!ts) return "";
  try {
    const d = new Date(ts);
    if (Number.isNaN(d.getTime())) return ts;
    return d.toLocaleString();
  } catch {
    return ts;
  }
}

function getFileKind(item) {
  const ext = String(item?.file_ext || item?.filename || item?.original_name || "")
    .toLowerCase()
    .split(".")
    .pop();

  if (item?.file_kind) return item.file_kind;
  if (["jpg", "jpeg", "png", "webp", "gif", "svg"].includes(ext)) return "image";
  if (ext === "pdf") return "pdf";
  if (["csv", "xlsx", "xls"].includes(ext)) return "spreadsheet";
  if (["doc", "docx", "txt", "rtf"].includes(ext)) return "document";
  if (["zip", "rar"].includes(ext)) return "archive";
  if (ext === "json") return "data";
  return "file";
}

function iconForKind(kind) {
  if (kind === "image") return "🖼️";
  if (kind === "pdf") return "📄";
  if (kind === "spreadsheet") return "📊";
  if (kind === "document") return "📝";
  if (kind === "archive") return "🗜️";
  if (kind === "data") return "🔢";
  return "📎";
}

async function copyText(txt) {
  try {
    await navigator.clipboard.writeText(txt || "");
    return true;
  } catch {
    try {
      const ta = document.createElement("textarea");
      ta.value = txt || "";
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
      return true;
    } catch {
      return false;
    }
  }
}

export default function PrivateMediaManager({
  open,
  onClose,
  apiFetch,
  isAdmin = false,
}) {
  const activeBranch = useMemo(() => safeLower(getActiveBranch() || ""), [open]);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyUpload, setBusyUpload] = useState(false);
  const [busyDelete, setBusyDelete] = useState({});
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [branch, setBranch] = useState(activeBranch || "jounieh");
  const [kind, setKind] = useState("");
  const [search, setSearch] = useState("");
  const [previewUrl, setPreviewUrl] = useState("");
  const fileRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setBranch(activeBranch || "jounieh");
  }, [open, activeBranch]);

  const loadItems = async (silent = false) => {
    if (!open) return;
    if (!silent) setLoading(true);
    setError("");

    try {
      const qs = new URLSearchParams();
      qs.set("limit", "200");
      if (branch) qs.set("branch", branch);
      if (kind) qs.set("kind", kind);
      if (search.trim()) qs.set("search", search.trim());

      const data = await apiFetch(`/media-manager/list?${qs.toString()}`, {
        method: "GET",
      });

      setItems(Array.isArray(data?.items) ? data.items : []);
    } catch (e) {
      setError(String(e?.message || e || "Failed to load files"));
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    if (open) loadItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, branch, kind]);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => loadItems(true), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const onPickFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setBusyUpload(true);
    setError("");
    setSuccess("");

    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("branch", branch || activeBranch || "jounieh");

      const data = await apiFetch("/media-manager/upload", {
        method: "POST",
        body: fd,
      });

      const newUrl = String(data?.url || "");
      if (newUrl) {
        setPreviewUrl(newUrl);
        await copyText(newUrl);
      }

      setSuccess("✅ File uploaded successfully. Online link copied.");
      await loadItems(true);
    } catch (e2) {
      setError(String(e2?.message || e2 || "Upload failed"));
    } finally {
      setBusyUpload(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const onCopyLink = async (url) => {
    const ok = await copyText(url || "");
    if (ok) setSuccess("✅ Link copied.");
    else setError("Could not copy link.");
  };

  const onDelete = async (item) => {
    if (!isAdmin) {
      setError("Only admin can delete files.");
      return;
    }

    if (!window.confirm(`Delete "${item?.original_name || item?.filename || "this file"}"?`)) return;

    setBusyDelete((s) => ({ ...s, [item.filename]: true }));
    setError("");
    setSuccess("");

    try {
      await apiFetch(`/media-manager/${encodeURIComponent(item.filename)}`, {
        method: "DELETE",
      });

      setItems((prev) => prev.filter((x) => x.filename !== item.filename));
      setSuccess("✅ File deleted.");
    } catch (e) {
      setError(String(e?.message || e || "Delete failed"));
    } finally {
      setBusyDelete((s) => ({ ...s, [item.filename]: false }));
    }
  };

  if (!open) return null;

  return (
    <div className="dialog-overlay" onClick={onClose} style={{ zIndex: 1300 }}>
      <div
        className="dialog-card"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "min(98vw, 1150px)",
          maxWidth: "98vw",
          height: "min(92vh, 880px)",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <div
          className="dialog-head"
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            flexWrap: "wrap",
          }}
        >
          <div className="dialog-title">📁 Online Files Manager</div>

          <div
            style={{
              marginLeft: "auto",
              display: "flex",
              gap: 8,
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <span style={{ fontSize: 12, opacity: 0.85 }}>Branch</span>
              <select value={branch} onChange={(e) => setBranch(safeLower(e.target.value))}>
                <option value="jounieh">Jounieh</option>
                <option value="tripoli">Tripoli</option>
                <option value="batroun">Batroun</option>
                <option value="zgharta">Zgharta</option>
                <option value="ehden">Ehden</option>
              </select>
            </label>

            <button className="btn tiny" onClick={() => loadItems()}>
              Refresh
            </button>

            <button className="btn tiny" onClick={onClose}>
              Close
            </button>
          </div>
        </div>

        <div style={{ padding: 12, display: "grid", gap: 12, overflow: "auto" }}>
          <div
            style={{
              border: "1px solid rgba(255,255,255,.10)",
              borderRadius: 12,
              padding: 12,
              background: "rgba(255,255,255,.03)",
              display: "grid",
              gap: 10,
            }}
          >
            <div style={{ fontWeight: 800 }}>Upload new file</div>

            <input
              ref={fileRef}
              type="file"
              accept=".jpg,.jpeg,.png,.webp,.gif,.svg,.pdf,.csv,.xlsx,.xls,.doc,.docx,.txt,.rtf,.zip,.rar,.json,image/*,application/pdf"
              onChange={onPickFile}
              disabled={busyUpload}
            />

            <div style={{ fontSize: 12, opacity: 0.8 }}>
              Allowed: images, PDF, CSV, Excel, Word, TXT, ZIP, JSON.
            </div>

            {!!error && (
              <div style={{
                color: "#fecaca",
                background: "rgba(127,29,29,.35)",
                border: "1px solid rgba(248,113,113,.35)",
                borderRadius: 10,
                padding: "8px 10px",
              }}>
                {error}
              </div>
            )}

            {!!success && (
              <div style={{
                color: "#bbf7d0",
                background: "rgba(20,83,45,.35)",
                border: "1px solid rgba(74,222,128,.35)",
                borderRadius: 10,
                padding: "8px 10px",
              }}>
                {success}
              </div>
            )}

            {busyUpload && <div style={{ opacity: 0.85 }}>Uploading…</div>}

            {!!previewUrl && (
              <div style={{ display: "grid", gap: 8, borderTop: "1px solid rgba(255,255,255,.08)", paddingTop: 10 }}>
                <div style={{ fontWeight: 700 }}>Latest online URL</div>

                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                  <input
                    value={previewUrl}
                    readOnly
                    style={{
                      flex: 1,
                      minWidth: 240,
                      padding: "10px 12px",
                      borderRadius: 10,
                      border: "1px solid rgba(255,255,255,.12)",
                      background: "rgba(255,255,255,.04)",
                      color: "#fff",
                    }}
                  />
                  <button className="btn" onClick={() => onCopyLink(previewUrl)}>
                    Copy Link
                  </button>
                  <a className="btn" href={previewUrl} target="_blank" rel="noreferrer">
                    Open
                  </a>
                </div>
              </div>
            )}
          </div>

          <div
            style={{
              border: "1px solid rgba(255,255,255,.10)",
              borderRadius: 12,
              padding: 12,
              background: "rgba(255,255,255,.03)",
              display: "grid",
              gap: 10,
            }}
          >
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
              <div style={{ fontWeight: 800 }}>Uploaded files</div>
              <div style={{ opacity: 0.75, fontSize: 12 }}>
                {loading ? "Loading..." : `${items.length} item(s)`}
              </div>

              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search file..."
                style={{
                  marginLeft: "auto",
                  minWidth: 220,
                  padding: "9px 11px",
                  borderRadius: 10,
                  border: "1px solid rgba(255,255,255,.12)",
                  background: "rgba(255,255,255,.04)",
                  color: "#fff",
                }}
              />

              <select value={kind} onChange={(e) => setKind(e.target.value)}>
                <option value="">All types</option>
                <option value="image">Images</option>
                <option value="pdf">PDF</option>
                <option value="spreadsheet">CSV / Excel</option>
                <option value="document">Documents</option>
                <option value="archive">Archive</option>
                <option value="data">JSON</option>
              </select>
            </div>

            {loading ? (
              <div style={{ opacity: 0.8 }}>Loading files…</div>
            ) : items.length === 0 ? (
              <div style={{ opacity: 0.75 }}>No files found.</div>
            ) : (
              <div style={{ display: "grid", gap: 10 }}>
                {items.map((item) => {
                  const fileUrl = item?.url || "";
                  const deleting = !!busyDelete[item.filename];
                  const fileKind = getFileKind(item);
                  const isImage = fileKind === "image";

                  return (
                    <div
                      key={item.filename}
                      style={{
                        border: "1px solid rgba(255,255,255,.08)",
                        borderRadius: 12,
                        padding: 10,
                        display: "grid",
                        gap: 10,
                        background: "rgba(255,255,255,.02)",
                      }}
                    >
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: "120px 1fr",
                          gap: 12,
                          alignItems: "start",
                        }}
                      >
                        <div
                          style={{
                            width: 120,
                            height: 120,
                            borderRadius: 10,
                            overflow: "hidden",
                            border: "1px solid rgba(255,255,255,.08)",
                            background: "rgba(255,255,255,.04)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: 42,
                          }}
                        >
                          {isImage ? (
                            <img
                              src={fileUrl}
                              alt={item.original_name || item.filename}
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                                display: "block",
                              }}
                            />
                          ) : (
                            <span>{iconForKind(fileKind)}</span>
                          )}
                        </div>

                        <div style={{ minWidth: 0, display: "grid", gap: 6 }}>
                          <div style={{ fontWeight: 800, wordBreak: "break-word" }}>
                            {item.original_name || item.filename}
                          </div>

                          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, fontSize: 12, opacity: 0.8 }}>
                            <span>{iconForKind(fileKind)} {fileKind}</span>
                            <span>📍 {item.branch || "-"}</span>
                            <span>📦 {fmtBytes(item.size_bytes)}</span>
                            <span>🕒 {fmtWhen(item.created_at)}</span>
                            {!!item.uploaded_by && <span>👤 {item.uploaded_by}</span>}
                          </div>

                          <div style={{ fontSize: 12, opacity: 0.7, wordBreak: "break-all" }}>
                            File: {item.filename}
                          </div>

                          <input
                            value={fileUrl}
                            readOnly
                            style={{
                              width: "100%",
                              padding: "10px 12px",
                              borderRadius: 10,
                              border: "1px solid rgba(255,255,255,.12)",
                              background: "rgba(255,255,255,.04)",
                              color: "#fff",
                            }}
                          />

                          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                            <button className="btn tiny" onClick={() => onCopyLink(fileUrl)}>
                              Copy Link
                            </button>

                            <a className="btn tiny" href={fileUrl} target="_blank" rel="noreferrer">
                              Open
                            </a>

                            {isAdmin && (
                              <button
                                className="btn tiny"
                                disabled={deleting}
                                onClick={() => onDelete(item)}
                              >
                                {deleting ? "Deleting..." : "Delete"}
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}